import type { ComponentProps } from 'react';

import { cva, type VariantProps } from 'class-variance-authority';

import { useFieldBaseWiring } from '../hooks/use-field-wiring';
import { cn } from '../lib/utils';
import {
    Field,
    FieldContent,
    FieldDescription,
    FieldLabel,
    type FieldOverrides,
    FieldTitle,
} from './field';
import { RadioGroupItem } from './radio-group';

const radioGroupItemFieldVariants = cva('gap-2', {
    variants: {
        variant: {
            default: '',
            box: cn(
                'rounded-sm p-3 transition-colors',
                'data-[disabled=true]:cursor-not-allowed',
                'not-data-[disabled=true]:has-[[data-state=checked]]:border-primary not-data-[disabled=true]:has-[[data-state=checked]]:bg-primary/10',
                'not-data-[disabled=true]:hover:bg-accent/50',
                'not-data-[disabled=true]:has-[[data-state=checked]]:hover:bg-accent/50',
                'has-[:focus-visible]:border-ring has-[:focus-visible]:[box-shadow:0_0_0_2px_var(--accent)]',
                'data-[invalid=true]:border-destructive data-[invalid=true]:bg-destructive-foreground',
            ),
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});

type Props = ComponentProps<typeof RadioGroupItem> &
    VariantProps<typeof radioGroupItemFieldVariants> & {
        label: string;
        description?: string;
        error?: boolean;
        fieldProps?: FieldOverrides;
        fieldClassName?: string;
    };

function RadioGroupItemField({
    label,
    description,
    error,
    id,
    fieldProps,
    fieldClassName,
    variant,
    ...itemProps
}: Props) {
    const wiring = useFieldBaseWiring({ id, description, error });
    const { className: fieldPropsClassName, ...restFieldProps } = fieldProps ?? {};
    const isBox = variant === 'box';

    const field = (
        <Field
            orientation="horizontal"
            data-disabled={itemProps.disabled}
            data-invalid={wiring.dataInvalid}
            className={cn(
                'group',
                radioGroupItemFieldVariants({ variant }),
                fieldPropsClassName,
                fieldClassName,
            )}
            alignControlToLabel={isBox}
            {...restFieldProps}
        >
            <RadioGroupItem {...itemProps} {...wiring.controlProps} />
            <FieldContent className={isBox ? undefined : 'gap-1.5'}>
                {isBox ? (
                    <FieldTitle destructiveWhenInvalid>{label}</FieldTitle>
                ) : (
                    <FieldLabel
                        destructiveWhenInvalid
                        htmlFor={wiring.controlId}
                        dimWhenDisabled
                        className="cursor-pointer leading-none group-data-[disabled=true]/field:cursor-not-allowed"
                    >
                        {label}
                    </FieldLabel>
                )}
                {description && (
                    <FieldDescription id={wiring.descriptionId} destructiveWhenInvalid={false}>
                        {description}
                    </FieldDescription>
                )}
            </FieldContent>
        </Field>
    );

    if (isBox) {
        return (
            <FieldLabel
                htmlFor={wiring.controlId}
                className="block cursor-pointer has-[[data-disabled=true]]:cursor-not-allowed"
            >
                {field}
            </FieldLabel>
        );
    }

    return field;
}

export { RadioGroupItemField, radioGroupItemFieldVariants };
